import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { fetchAuthSession } from 'aws-amplify/auth'
import { userApi, publicApi } from '../services/api'
import { Check, Zap, ArrowRight, X } from 'lucide-react'
import { Logo } from '../components/Logo'
import { ConfirmDialog } from '../components/ConfirmDialog'
import toast from 'react-hot-toast'

declare global {
  interface Window {
    Paddle: any
  }
}

interface PricingTier {
  name: string
  price: string
  priceId: string
  description: string
  features: string[]
  popular?: boolean
  loading?: boolean
  monthlyPrice?: number
}

export function PricingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const activeCheckoutTierRef = useRef<string | null>(null)
  const [currentTier, setCurrentTier] = useState<string | null>(null)
  const [loadingSubscription, setLoadingSubscription] = useState(true)
  const [loadingPricing, setLoadingPricing] = useState(true)
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([])
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
    variant?: 'danger' | 'warning' | 'info'
    confirmText?: string
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} })

  // Fetch pricing plans from API on mount
  useEffect(() => {
    const fetchPricingPlans = async () => {
      setLoadingPricing(true)
      try {
        const plans = await publicApi.getPricingPlans()
        // Convert API response to PricingTier format and sort by price
        const tiers = plans
          .map(plan => ({
            name: plan.planName,
            price: plan.monthlyPrice === 0 ? '$0' : `$${plan.monthlyPrice}`,
            priceId: plan.paddlePriceId,
            description: plan.description,
            features: plan.features,
            popular: plan.isPopular,
            loading: plan.paddlePriceId ? true : false, // Mark paid tiers for Paddle price fetching
            monthlyPrice: plan.monthlyPrice, // Keep for sorting
          }))
          .sort((a, b) => a.monthlyPrice! - b.monthlyPrice!) // Sort by price ascending
        setPricingTiers(tiers)
      } catch (error) {
        console.error('Error fetching pricing plans:', error)
        // Fallback to hardcoded defaults (sorted by price)
        const fallbackTiers: PricingTier[] = [
          {
            name: 'Builder',
            price: '$5',
            priceId: '',
            description: 'Perfect for getting started',
            features: [
              '100 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard',
              'Email support',
              'Standard rate limits',
            ],
            loading: false,
            monthlyPrice: 5,
          },
          {
            name: 'Launch',
            price: '$29',
            priceId: '',
            description: 'For professionals and growing teams',
            features: [
              '1,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard',
              'Email support',
              'Standard rate limits',
            ],
            popular: true,
            loading: false,
            monthlyPrice: 29,
          },
          {
            name: 'Growth',
            price: '$99',
            priceId: '',
            description: 'For scaling applications',
            features: [
              '5,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard',
              'Email support',
              'Standard rate limits',
            ],
            loading: false,
            monthlyPrice: 99,
          },
          {
            name: 'Scale',
            price: '$299',
            priceId: '',
            description: 'For enterprise-scale deployments',
            features: [
              '20,000 credits per month',
              'Access to all AI models',
              'API key management',
              'Usage analytics dashboard',
              'Email support',
              'Standard rate limits',
            ],
            loading: false,
            monthlyPrice: 299,
          },
        ]
        setPricingTiers(fallbackTiers)
      } finally {
        setLoadingPricing(false)
      }
    }

    fetchPricingPlans()
  }, [])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
    script.async = true
    document.body.appendChild(script)

    script.onload = () => {
      if (window.Paddle) {
        const environment = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox'
        const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN || ''
        
        window.Paddle.Environment.set(environment)
        window.Paddle.Initialize({
          token: token,
          eventCallback: (_event: any) => {
            if (_event.name === 'checkout.completed') {
              const tierName = activeCheckoutTierRef.current
              activeCheckoutTierRef.current = null
              setLoading(null)
              if (tierName) {
                setCurrentTier(tierName.toLowerCase())
              }
              toast.success('Payment successful! Your subscription is now active. Redirecting to dashboard...')
              setTimeout(() => navigate('/dashboard'), 1500)
            } else if (_event.name === 'checkout.closed') {
              if (activeCheckoutTierRef.current) {
                activeCheckoutTierRef.current = null
                setLoading(null)
              }
            }
          },
        })

        // Fetch Paddle prices after pricing plans are loaded
        if (!loadingPricing && pricingTiers.length > 0) {
          fetchPaddlePrices()
        }
      }
    }

    return () => {
      document.body.removeChild(script)
    }
  }, [loadingPricing, pricingTiers])

  const fetchPaddlePrices = async () => {
    if (!window.Paddle) return

    try {
      // Fetch real-time prices from Paddle for each tier with a priceId
      pricingTiers.forEach((tier) => {
        if (tier.priceId && tier.loading) {
          window.Paddle.PricePreview({
            items: [{ priceId: tier.priceId, quantity: 1 }],
          }).then((result: any) => {
            if (result?.data?.details?.lineItems?.[0]) {
              const rawPrice = result.data.details.lineItems[0].formattedTotals.total
              // Remove decimals from price (e.g., "$29.00" -> "$29")
              const price = rawPrice.replace(/\.00$/, '').replace(/\.\d{2}$/, (match: string) => {
                // Only keep decimals if they're not .00
                return match === '.00' ? '' : match
              })
              setPricingTiers((prev) =>
                prev.map((t) =>
                  t.name === tier.name
                    ? { ...t, price: price, loading: false }
                    : t
                )
              )
            }
          }).catch(() => {
            // On error, keep the price from API and mark as not loading
            setPricingTiers((prev) =>
              prev.map((t) =>
                t.name === tier.name
                  ? { ...t, loading: false }
                  : t
              )
            )
          })
        }
      })
    } catch (error) {
      console.error('Error fetching Paddle prices:', error)
    }
  }

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus()
    } else {
      setCurrentTier('free')
      setLoadingSubscription(false)
    }
  }, [user])

  const fetchSubscriptionStatus = async () => {
    if (!user) {
      setCurrentTier('free')
      setLoadingSubscription(false)
      return
    }

    setLoadingSubscription(true)
    try {
      const data = await userApi.getSubscriptionStatus()
      const tier = data.userTier?.toLowerCase() || 'free'
      setCurrentTier(tier)
    } catch (error) {
      console.error('Error fetching subscription:', error)
      setCurrentTier('free')
    } finally {
      setLoadingSubscription(false)
    }
  }

  const handleSubscribe = async (tier: PricingTier) => {
    if (!tier.priceId) {
      if (user) {
        navigate('/dashboard')
      } else {
        navigate('/signup')
      }
      return
    }

    if (!user) {
      navigate('/signup')
      return
    }

    const tierName = tier.name.toLowerCase()

    // User already has a paid subscription - use the upgrade/downgrade API (prorated)
    if (currentTier && currentTier !== 'free' && currentTier !== tierName) {
      const tierOrder = ['builder', 'launch', 'growth', 'scale']
      const currentIndex = tierOrder.indexOf(currentTier)
      const targetIndex = tierOrder.indexOf(tierName)
      const isUpgrade = targetIndex > currentIndex

      setConfirmDialog({
        isOpen: true,
        title: isUpgrade ? `Upgrade to ${tier.name}` : `Downgrade to ${tier.name}`,
        message: isUpgrade
          ? `Upgrade to ${tier.name}? You'll be charged the prorated difference immediately.`
          : `Downgrade to ${tier.name}? The change will take effect at the end of your current billing period.`,
        variant: isUpgrade ? 'info' : 'warning',
        confirmText: isUpgrade ? 'Upgrade Now' : 'Schedule Downgrade',
        onConfirm: async () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }))
          setLoading(tier.name)
          try {
            if (isUpgrade) {
              await userApi.upgradeSubscription(tier.priceId)
              toast.success('Upgrade successful!')
              setCurrentTier(tierName)
            } else {
              await userApi.downgradeSubscription(tier.priceId)
              toast.success('Downgrade scheduled for end of billing period.')
            }
            await fetchSubscriptionStatus()
          } catch (error: any) {
            toast.error(error.response?.data?.error || 'Operation failed')
          } finally {
            setLoading(null)
          }
        },
      })
      return
    }

    openCheckout(tier)
  }

  const openCheckout = async (tier: PricingTier) => {
    if (!window.Paddle) {
      toast.error('Payment system is loading. Please try again in a moment.')
      return
    }

    setLoading(tier.name)
    activeCheckoutTierRef.current = tier.name

    try {
      const session = await fetchAuthSession()
      const userId = session.tokens?.idToken?.payload.sub as string
      const email = session.tokens?.idToken?.payload.email as string

      window.Paddle.Checkout.open({
        items: [{ priceId: tier.priceId, quantity: 1 }],
        customer: {
          email: email,
        },
        customData: {
          user_id: userId,
        },
        settings: {
          displayMode: 'overlay',
          theme: 'dark',
          locale: 'en',
          allowLogout: false,
        },
      })
    } catch (error: any) {
      console.error('Error opening checkout:', error)
      toast.error(`Failed to open checkout: ${error.message || 'Unknown error'}`)
      activeCheckoutTierRef.current = null
      setLoading(null)
    }
  }

  const handleCancelSubscription = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Subscription',
      message: "Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period. Your subscription will remain active until then.",
      variant: 'danger',
      confirmText: 'Cancel Subscription',
      onConfirm: () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        confirmCancelSubscription()
      },
    })
  }

  const confirmCancelSubscription = async () => {
    setCancelling(true)

    try {
      const result = await userApi.cancelSubscription()
      toast.success(result.message)
      await fetchSubscriptionStatus()
    } catch (error: any) {
      console.error('Error cancelling subscription:', error)
      toast.error('Failed to cancel subscription. Please try again or contact support.')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
        loading={cancelling}
      />
      {/* Header */}
      <div className="border-b border-border bg-panel/50 backdrop-blur-lg sticky top-0 z-10">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200"
              >
                Back to Dashboard
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-transparent hover:bg-panel text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 shadow-lg shadow-accent/20"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-16 sm:py-20">
        <div className="text-center mb-16 animate-fade-in-down">
          <h2 className="text-4xl sm:text-5xl font-bold text-text mb-4">
            Simple, <span className="text-gradient">Transparent</span> Pricing
          </h2>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Choose the plan that fits your needs. Scale up or down anytime with no hidden fees.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-16">
          {loadingPricing ? (
            // Show loading skeleton while fetching pricing plans
            <div className="col-span-full flex justify-center items-center py-20">
              <div className="text-center">
                <div className="spinner h-12 w-12 mx-auto mb-4"></div>
                <p className="text-text-muted">Loading pricing plans...</p>
              </div>
            </div>
          ) : (
            pricingTiers.map((tier, index) => (
              <div
                key={tier.name}
                className={`card transition-all duration-300 hover:scale-105 ${
                  tier.popular
                    ? 'ring-2 ring-accent shadow-xl shadow-accent/20 relative'
                    : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-accent to-accent-2 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      MOST POPULAR
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-text mb-2">
                      {tier.name}
                    </h3>
                    <p className="text-text-muted text-sm mb-6">
                      {tier.description}
                    </p>

                    <div className="mb-6">
                      {tier.loading ? (
                        <div className="flex justify-center">
                          <div className="spinner h-12 w-12"></div>
                        </div>
                      ) : (
                        <div className="flex items-baseline justify-center">
                          <span className="text-5xl font-extrabold text-gradient">
                            {tier.price.split('/')[0]}
                          </span>
                          {tier.price !== '$0' && (
                            <span className="text-xl text-text-muted ml-2">
                              /month
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-text-muted text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={
                      loadingSubscription ||
                      loading === tier.name ||
                      tier.loading === true ||
                      (user && currentTier === tier.name.toLowerCase()) ||
                      false
                    }
                    className={`w-full px-5 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                      loadingSubscription
                        ? 'bg-panel border border-border text-text-muted opacity-50 cursor-wait'
                        : user && currentTier === tier.name.toLowerCase()
                        ? 'bg-panel border border-border text-text-muted opacity-50 cursor-not-allowed'
                        : tier.popular
                        ? 'bg-accent hover:bg-accent/90 text-white shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30'
                        : 'bg-transparent hover:bg-accent/10 text-accent border border-accent/30 hover:border-accent/50'
                    }`}
                  >
                    {loadingSubscription ? (
                      'Loading...'
                    ) : loading === tier.name ? (
                      <>
                        <div className="spinner h-5 w-5" />
                        Processing...
                      </>
                    ) : user && currentTier === tier.name.toLowerCase() ? (
                      <>
                        <Check className="h-5 w-5" />
                        Current Plan
                      </>
                    ) : tier.name === 'Free' && !user ? (
                      <>
                        Get Started Free
                        <ArrowRight className="h-5 w-5" />
                      </>
                    ) : tier.name === 'Free' && user ? (
                      'Free Plan'
                    ) : (
                      <>
                        Subscribe Now
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Features Section */}
        <div className="card p-12 max-w-4xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-text text-center mb-8">
            All Plans Include
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Secure API Access',
                description: 'Enterprise-grade security with encrypted connections',
              },
              {
                title: 'Automatic Scaling',
                description: 'Infrastructure that grows with your needs',
              },
              {
                title: '99.9% Uptime SLA',
                description: 'Reliable service you can count on',
              },
              {
                title: 'Cancel Anytime',
                description: 'No long-term contracts or commitments',
              },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Check className="h-6 w-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-text mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-text-muted">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cancel Subscription */}
        {!loadingSubscription && user && currentTier !== 'free' && currentTier !== null && (
          <div className="text-center mb-16">
            <button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="px-5 py-2.5 bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 rounded-lg font-medium transition-all duration-200 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {cancelling ? (
                <>
                  <div className="spinner h-4 w-4" />
                  Cancelling...
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  Cancel Subscription
                </>
              )}
            </button>
            <p className="text-sm text-text-muted mt-3">
              You'll keep access until the end of your billing period
            </p>
          </div>
        )}
        
        {/* Contact Section */}
        <div className="text-center">
          <p className="text-text-muted text-lg mb-4">
            Have questions about our pricing?
          </p>
          <a
            href="mailto:support@softitect.com"
            className="link text-lg font-semibold inline-flex items-center gap-2"
          >
            Contact our sales team
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  )
}
